<template>
  <div class="purchase-order-form" :class="{ history: isHistoryRecord }">
    <form>
      <section>
        <h1>{{ $t('purchaseOrders.details.purchaseOrderDetails') }}</h1>
        <div class="po-client-container">
          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.poNumber') }}</label>
            <InputField
              v-model="formData.purchase_order_number"
              type="text"
              class="bordered"
              @change="onChange"
            />
          </div>

          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.clientNumber') }}</label>
            <InputField
              v-model="formData.client_number"
              type="text"
              class="bordered"
              @change="onChange"
            />
          </div>

          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.clientName') }}</label>
            <TolTypeAhead
              v-if="!formData.client_name"
              :search-params="{
                url: '/clients/client-quick-search',
                searchKeys: ['name']
              }"
              class="blue contact-box"
              :menu-label="'Client Name'"
              @select-item="onClientSelect($event, 'CLIENT')"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </TolTypeAhead>
            <InputField
              v-else
              v-model="formData.client_name"
              type="text"
              class="bordered"
              readonly
            />
          </div>
        </div>
      </section>
      <section>
        <div class="end-client-dates-container">
          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.endClient') }}</label>
            <TolTypeAhead
              v-if="!formData.end_client_name"
              :search-params="{
                url: '/assignments/end-client-lookup',
                searchKeys: ['name']
              }"
              class="blue contact-box"
              :menu-label="'End Client Name'"
              @select-item="onClientSelect($event, 'END_CLIENT')"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </TolTypeAhead>
            <InputField
              v-else
              v-model="formData.end_client_name"
              type="text"
              class="bordered"
              readonly
            />
          </div>

          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.startDate') }}</label>
            <form-date-picker v-model="purchaseOrderStartDate" @change="onStartDateChange">
            </form-date-picker>
          </div>

          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.endDate') }}</label>
            <form-date-picker v-model="purchaseOrderEndDate" @change="onEndDateChange">
            </form-date-picker>
          </div>
        </div>
      </section>

      <section>
        <div class="funds-status-container">
          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.fundsIssued') }}</label>
            <InputField v-model="formData.funds_issued" type="text" class="bordered" readonly />
          </div>

          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.fundsRemaning') }}</label>
            <InputField v-model="formData.funds_remaining" type="text" class="bordered" readonly />
          </div>
          <div class="form-control">
            <label>{{ $t('purchaseOrders.details.status') }}</label>
            <DropdownSelect
              :menu-items="statuses"
              :selected-item="camelCaseOptions(formData.status)"
              name="status"
              class="blue bordered"
              @select-item="onStatusChange"
            />
          </div>

          <div v-if="!isHistoryRecord" class="form-control">
            <div :class="percentClass(formData.percentage_remaining)">
              {{ formData.percentage_remaining + '%' }}
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="!isHistoryRecord && formData.reference_number !== null"
        class="details-section"
      >
        <div class="form-control">
          <div class="list-header">
            {{ $t('purchaseOrders.details.lineItems') }}
            <span @click="isNewItemFormVisible = !isNewItemFormVisible">+</span>
          </div>
          <tol-table
            v-if="formData.POItems"
            :table-data="formData.POItems"
            :columns="columns"
            :is-new-row="isNewItemFormVisible"
            :display="true"
            :external-param="{ id: depleteAddParam.id, selected: depleteAddParam.selected }"
            :extra-param-display="true"
            class="line-items"
          >
            <template v-slot:tableHeader>
              <th>{{ $t('purchaseOrders.poItems.item') }}</th>
              <th>{{ $t('purchaseOrders.poItems.type') }}</th>
              <th>{{ $t('purchaseOrders.poItems.Region') }}</th>
              <th>{{ $t('purchaseOrders.poItems.UnitPrice') }}</th>
              <th>{{ $t('purchaseOrders.poItems.quantityIssued') }}</th>
              <th>{{ $t('purchaseOrders.poItems.quantityRemanining') }}</th>
              <th>{{ $t('purchaseOrders.poItems.amount') }}</th>
              <th>{{ $t('purchaseOrders.poItems.amountRemanining') }}</th>
              <th>{{ $t('purchaseOrders.poItems.percentRemaining') }}</th>
              <th></th>
              <th></th>
              <th></th>
            </template>
            <template v-slot:add="{ value }"> </template>
            <template v-slot:item_number="{ value }">
              <InputField
                v-if="value.isEditable"
                :value="value.item_number"
                type="text"
                class="bordered"
                @change="onChange($event, value, 'item_number')"
              />
            </template>

            <template v-slot:rate_code="{ value }">
              <dropdown-select
                v-if="value.isEditable"
                :menu-items="rateTypes"
                :selected-item="value.rate_code"
                class="blue bordered"
                @select-item="changeNewRateCode($event, value, 'rate_code')"
              />
            </template>

            <template v-slot:province_name="{ value }">
              <dropdown-select
                v-if="value.isEditable"
                :menu-items="provinceNames"
                :selected-item="value.province_name"
                class="blue bordered"
                @select-item="onProvinceChange($event, value, 'province_name')"
              />
            </template>

            <template v-slot:unit_price="{ value }">
              <InputField
                v-if="value.isEditable"
                :value="value.unit_price"
                type="text"
                class="bordered"
                @change="onChange($event, value, 'unit_price')"
              />
            </template>

            <template v-slot:quantity_issued="{ value }">
              <InputField
                v-if="value.isEditable"
                :value="value.quantity_issued"
                type="text"
                class="bordered"
                @change="onChange($event, value, 'quantity_issued')"
              />
            </template>

            <template v-slot:percentage_remaining="{ value }">
              <div :class="percentClass(value.percentage_remaining)">
                {{ value.percentage_remaining }}
              </div>
            </template>

            <template v-slot:add="{ value }">
              <button type="button" @click="changeDisplay(value, 'FUNDS_ISSUED')">
                {{ $t('purchaseOrders.details.issue') }}
              </button>
            </template>

            <template v-slot:emptyRow>
              <tr></tr>
            </template>

            <template v-slot:delete="{ value }">
              <button type="button" @click="changeDisplay(value, 'MANUAL_DEPLETE')">
                {{ $t('purchaseOrders.details.deplete') }}
              </button>
            </template>
            <template v-slot:save="{ value }">
              <button type="button" @click="EditLineItem()">
                {{ $t('purchaseOrders.details.save') }}
              </button>
            </template>

            <template v-slot:displayExpand="{ value }">
              <h1>
                {{
                  action == 'FUNDS_ISSUED'
                    ? $t('purchaseOrders.details.issueFunds')
                    : $t('purchaseOrders.details.depleteFunds')
                }}
              </h1>
              <div class="form-control">
                {{ action == 'FUNDS_ISSUED' ? 'Add' : 'Deplete' }} Units {{ value.unit_price }} per
                unit
                <InputField
                  v-model="poItemDetails.unit"
                  type="text"
                  class="bordered form-number-input wide"
                />
                <label class="labelcolor">{{
                  action == 'FUNDS_ISSUED'
                    ? $t('purchaseOrders.details.warning.issueFunds')
                    : $t('purchaseOrders.details.warning.depleteFunds')
                }}</label>
                <label>{{
                  action == 'FUNDS_ISSUED'
                    ? $t('purchaseOrders.details.issueMessage.message')
                    : $t('purchaseOrders.details.depleteMessage.message')
                }}</label>
              </div>

              <section class="details-section">
                <div class="po-order-item-container">
                  <div class="comment">
                    <InputField
                      v-model="poItemDetails.comment"
                      class="bordered"
                      field-icon="comments"
                    />
                  </div>
                  <div>
                    <tol-button
                      id="save-btn"
                      :button-type="'minor'"
                      icon-name="save"
                      :class="['poitem-action']"
                      tooltip="SAVE"
                      @click="issueOrDeplete(value)"
                    >
                    </tol-button>
                  </div>
                </div>
              </section>
            </template>

            <template v-slot:emptyRow>
              <td>
                <InputField v-model="newLineItem.item_number" type="text" class="form-input" />
              </td>

              <td>
                <DropdownSelect
                  :menu-items="rateTypes"
                  :selected-item="rateTypes[0]"
                  class="blue bordered"
                  @select-item="changeNewRateCode($event)"
                />
              </td>

              <td>
                <DropdownSelect
                  :menu-items="provinceNames"
                  :selected-item="provinceNames[0]"
                  class="blue bordered"
                  @select-item="onProvinceChange($event)"
                />
              </td>

              <td>
                <InputField
                  v-model="newLineItem.unit_price"
                  type="text"
                  class="bordered"
                  @input="onUnitPriceChange()"
                />
              </td>

              <td>
                <InputField
                  v-model="newLineItem.quantity_issued"
                  type="text"
                  class="bordered"
                  @input="onQuanityChange()"
                />
              </td>

              <td></td>
              <td>
                <InputField
                  v-model="newLineItem.amount_issued"
                  type="text"
                  class="bordered"
                  readonly
                />
              </td>
              <td></td>
              <!--This is to align td cells style with tol table columns as new row has less columns than item list -->
              <td></td>
              <td></td>
            </template>

            <template v-slot:rowTest> </template>
          </tol-table>

          <section v-if="isNewItemFormVisible" class="details-section">
            <div class="po-order-item-container">
              <div class="comment">
                <InputField v-model="newLineItem.comment" class="bordered" field-icon="comments" />
              </div>
              <div>
                <tol-button
                  id="save-btn"
                  :button-type="'minor'"
                  icon-name="save"
                  :class="['poitem-action']"
                  tooltip="SAVE"
                  @click="saveNewLineItem()"
                >
                </tol-button>
              </div>
            </div>
          </section>
        </div>
      </section>

      <div v-if="!isHistoryRecord" class="module-action-container">
        <tol-button
          id="save-btn"
          button-type="save"
          label="Save"
          :class="[
            'module-action',
            { disabled: !isOnScreenDataModified && formData.reference_number }
          ]"
          @click="doPurchaseOrderSave()"
        >
        </tol-button>
      </div>
    </form>
  </div>
</template>

<script>
import InputField from '@/app/components/InputField';
import { clone } from 'ramda';
import { dateUtils } from '@/mixins/date-utils';
import format from 'date-fns/format';
import FormDatePicker from '@/app/components/FormDatePicker';
import DropdownSelect from '@/app/components/DropdownSelect';
import TolTable from '@/app/components/TolTable';
import { AppGetters } from '@/app/app.keys';
import TolButton from '@/app/components/TolButton';
import { TolErrorsGetters } from '../../../../tol-errors/tol-errors.keys';
import { disableForm } from '@/mixins/disable-form';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import {
  PurchaseOrdersDetailsActions,
  PurchaseOrdersDetailsGetters
} from '../purchase-order-details-keys';
import camelCase from '../../../../../../filters/camel-case.filter';

export default {
  name: 'PurchaseOrderDetailsForm',
  components: {
    InputField,
    FormDatePicker,
    DropdownSelect,
    TolTable,
    TolButton,
    TolTypeAhead
  },
  mixins: [dateUtils, disableForm],

  props: {
    purchaseOrder: {
      type: Object,
      required: true
    },
    isHistoryRecord: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      formData: {},
      poItemDetails: {
        comment: '',
        unit: 0
      },
      areFormFieldsDisabled: false,
      isOnScreenDataModified: false,
      statuses: ['Active', 'Closed'],
      isNewItemFormVisible: false,
      isExtraDisplay: false,
      isProcessFormBtnClicked: false,
      item_number: '',
      isLoaded: false,
      newLineItem: {
        item_number: '',
        rate_code: 'RG',
        province_name: 'Ontario',
        unit_price: null,
        quantity_issued: null,
        amount_issued: 0,
        comment: ''
      },
      editedLineItem: {},
      poItem: {},
      issueParam: {
        id: null,
        selected: false
      },
      depleteAddParam: {
        id: 0,
        selected: false
      },
      itemsSelected: new Map(),
      action: ''
    };
  },
  computed: {
    isItem() {
      return true;
    },
    isItemLoaded() {
      return this.$store.getters[PurchaseOrdersDetailsGetters.IsItemLoaded];
    },
    itemDetails() {
      return this.$store.getters[PurchaseOrdersDetailsGetters.GetPoItems];
    },
    selectedStatus() {
      return this.formData.status;
    },
    purchaseOrderStartDate() {
      return this.formData.start_date ? this.dateFromString(this.formData.start_date) : null;
    },
    purchaseOrderEndDate() {
      return this.formData.end_date ? this.dateFromString(this.formData.end_date) : null;
    },
    columns() {
      return [
        'item_number',
        'rate_code',
        'province_name',
        'unit_price',
        'quantity_issued',
        'quantity_remaining',
        'amount_issued',
        'amount_remaining',
        'percentage_remaining'
      ];
    },
    rateTypes() {
      let rates = this.$store.getters[AppGetters.AssignmentRateTypes];
      return rates.map(rate => rate.code);
    },
    provinceNames() {
      return this.$store.getters[AppGetters.ProvinceNames];
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    },

    provinces() {
      return this.$store.getters[AppGetters.Provinces];
    }
  },

  watch: {
    purchaseOrder: {
      immediate: true,
      handler() {
        this.formData = clone(this.purchaseOrder);
        if (this.formData.status == null) {
          this.formData.status = 'ACTIVE';
        }
        if (this.formData.POItems) {
          this.formData.POItems.map(item => {
            item.province_name = this.provinces.filter(
              province => province.abbr == item.province_abbr
            )[0].name;
          });
        }
      }
    }
  },

  mounted() {
    this.formData = clone(this.purchaseOrder);
  },
  updated() {
    if (this.isHistoryRecord && !this.areFormFieldsDisabled) {
      const forms = document.querySelectorAll('.purchase-order-form.history form');
      forms.forEach(form => this.disableFormElements(form));
      this.areFormFieldsDisabled = true;
    }
  },
  methods: {
    camelCaseOptions(string) {
      return camelCase(string);
    },
    onChange(event, value, propertyName) {
      if (this.isHistoryRecord) {
        return;
      }
      this.isOnScreenDataModified = true;

      if (propertyName) {
        this.editedLineItem = value;
        this.editedLineItem[propertyName] = event.target.value;

        if (propertyName == 'unit_price' || propertyName == 'quantity_issued') {
          this.editedLineItem.amount_issued =
            this.editedLineItem.unit_price * this.editedLineItem.quantity_issued;
        }
      }
    },
    saveNewLineItem() {
      this.isNewItemFormVisible = false;
      this.newLineItem.poRefNum = this.formData.reference_number;

      this.newLineItem.province_abbr = this.provinces.filter(
        province => province.name == this.newLineItem.province_name
      )[0].abbr;

      delete this.newLineItem.province_name;
      this.$store.dispatch(PurchaseOrdersDetailsActions.SaveNewLineItem, this.newLineItem);
      this.newLineItem = {};
    },

    onProvinceChange(event, value, propertyName) {
      if (this.isHistoryRecord) {
        return;
      }
      if (propertyName) {
        this.editedLineItem = value;
        this.editedLineItem[propertyName] = event;
      } else {
        this.newLineItem.province_name = event;
      }
    },
    changeNewRateCode(event, value, propertyName) {
      if (this.isHistoryRecord) {
        return;
      }
      if (propertyName) {
        this.editedLineItem = value;
        this.editedLineItem[propertyName] = event;
      } else {
        this.newLineItem.rate_code = event;
      }
    },
    issueOrDeplete(value) {
      this.poItemDetails.poRefNum = this.formData.reference_number;
      this.poItemDetails.poItemId = value.id;
      this.poItemDetails.action = this.action;

      this.$store.dispatch(PurchaseOrdersDetailsActions.ManualIssueDeplete, this.poItemDetails);

      this.poItemDetails = {};
      this.depleteAddParam.selected = false;
    },
    changeDisplay(event, action) {
      this.action = action;

      if (this.itemsSelected.has(event.id)) {
        let val = this.itemsSelected.get(event.id);
        this.depleteAddParam.id = event.id;
        this.depleteAddParam.selected = !val;
        this.itemsSelected.set(event.id, this.depleteAddParam.selected);
      } else {
        this.depleteAddParam.id = event.id;
        this.depleteAddParam.selected = true;
        this.itemsSelected.set(event.id, this.depleteAddParam.selected);
      }
    },
    onEndDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.end_date = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onStartDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.start_date = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onStatusChange(status) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.status = status.toUpperCase();
      this.isOnScreenDataModified = true;
    },
    percentClass(percentage_remaining) {
      let className = '';
      if (percentage_remaining > 25) {
        className = 'green';
      } else if (percentage_remaining < 25 && percentage_remaining > 20) {
        className = 'yellow';
      } else {
        className = 'red';
      }

      return className;
    },
    addOnScreenValue() {},
    EditLineItem() {
      this.editedLineItem.poRefNum = this.formData.reference_number;

      this.editedLineItem.province_abbr = this.provinces.filter(
        province => province.name == this.editedLineItem.province_name
      )[0].abbr;
      delete this.editedLineItem.province_name;

      this.$store.dispatch(PurchaseOrdersDetailsActions.SaveNewLineItem, this.editedLineItem);
    },
    onUnitPriceChange() {
      if (this.isHistoryRecord) {
        return;
      }
      if (this.newLineItem.quantity_issued != 0) {
        this.newLineItem.amount_issued =
          this.newLineItem.unit_price * this.newLineItem.quantity_issued;
      }
    },
    onQuanityChange() {
      if (this.isHistoryRecord) {
        return;
      }
      if (this.newLineItem.unit_price != 0) {
        this.newLineItem.amount_issued =
          this.newLineItem.unit_price * this.newLineItem.quantity_issued;
      }
    },

    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },

    doPurchaseOrderSave() {
      this.isProcessFormBtnClicked = true;
      if (!this.isOnScreenDataModified) {
        return;
      }

      this.$store.dispatch(PurchaseOrdersDetailsActions.SavePurchaseOrder, this.formData);
      window.setTimeout(() => {
        this.isProcessFormBtnClicked = false;
        this.isOnScreenDataModified = false;
      }, 2000);
    },
    onClientSelect(event, element) {
      this.isOnScreenDataModified = true;
      if (element === 'CLIENT') {
        this.formData.client_name = event.name;
        this.formData.client_id = event.id;
      } else {
        this.formData.end_client_name = event.name;
        this.formData.end_client_id = event.id;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.po-client-container,
.funds-status-container,
.end-client-dates-container {
  width: 70%;
  display: flex;
  flex-wrap: wrap;

  & div {
    width: 15vw;
  }
}
.purchase-order-form {
  &.history {
    font-size: 0.8em;
    background-color: $light-grey-2;
    padding: 25px;
  }
  .submit-button {
    margin: 0px 1px 19px;
    background-color: red;
  }

  .po-order-item-container {
    display: flex;
    position: relative;
    justify-content: space-between;
    background-color: $background-level-3;
    padding: 60px 20px;
    width: 100%;
    margin: 30px 0;

    .poitem-action {
      width: 50px;
      height: 50px;
      font-size: 1.5em;
      margin-right: 20px;
      color: $white;
      border-radius: 100%;
      background-color: $secondary-green;
    }

    .comment {
      display: flex;
      background-color: $white;
      flex-basis: 50%;
      div {
        flex-grow: 1;
      }
    }
  }

  form {
    width: 100%;
    width: 100%;
    section {
      margin-bottom: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }
        .single-line {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          label {
            margin-right: 15px;
          }
        }
        .labelcolor {
          color: red;
        }
        .break {
          width: 100%;
          height: 0;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }
          .dropdown-container {
            width: 20%;
          }
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          border-bottom: 4px solid $row-border;
          font-weight: bold;
          span {
            display: inline-block;
            font-size: 1.2em;
            font-weight: bold;
            width: 20px;
            height: 20px;
            line-height: 20px;
            border-radius: 100%;
            text-align: center;
            &:hover {
              cursor: pointer;
              background-color: $light-grey-2;
            }
            &.history {
              display: none;
            }
          }
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }

      &.form-number-input {
        width: 50px;
        border: 1px solid $mid-blue;
        display: inline-flex;
        padding: 5px 0;
        &.wide {
          width: 100px;
        }
      }
      .bordered {
        border: 1px solid $blue;
        font-size: 1em;
        &:focus {
          outline: none;
        }
      }
    }

    .module-action-container {
      justify-content: flex-end;
    }
  }

  .nowrap {
    white-space: nowrap;
  }

  .red {
    width: 100%;
    height: 100%;
    background-color: $mid-red;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .green {
    color: $white;
    font-weight: bold;
    width: 100%;
    height: 100%;
    background-color: $secondary-green;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .yellow {
    width: 100%;
    height: 100%;
    background-color: $primary-warn;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .break {
    width: 100%;
    height: 0;
  }
}
</style>
